"use client"

import Link from "@/platform/link"
import { usePathname } from "@/platform/navigation"
import type { MouseEvent } from "react"
import { Bell } from "lucide-react"
import { useTranslations } from "@/lib/messages"
import { useUnsavedChanges } from "@/lib/unsaved-changes-context"
import { useWorkspaceNotifications } from "@/lib/workspace-notifications-context"
import { cn } from "@/lib/utils"
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"

function badgeLabel(count: number) {
  if (count <= 0) return null
  if (count > 99) return "99+"
  return String(count)
}

export function WorkspaceNotificationsNavItem() {
  const pathname = usePathname()
  const tNav = useTranslations("nav")
  const { requestNavigate } = useUnsavedChanges()
  const { enabled, unreadCount, pulseNav } = useWorkspaceNotifications()
  if (!enabled) return null

  const href = "/dashboard/notifications"
  const active = pathname === href || pathname.startsWith(`${href}/`)
  const badge = badgeLabel(unreadCount)

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={
          <Link
            href={href}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault()
              requestNavigate(href)
            }}
          />
        }
        isActive={active}
        tooltip={tNav("notifications")}
        className={cn(pulseNav && "workspace-notifications-nav-pulse")}
      >
        <span className="relative flex size-6 shrink-0 items-center justify-center">
          <Bell className="size-4" />
          {badge ? (
            <span
              className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold leading-none text-white"
              aria-label={`${unreadCount} unread`}
            >
              {badge}
            </span>
          ) : null}
        </span>
        <span>{tNav("notifications")}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
